import type {
  IdFactory, ItineraryDay, ItineraryEditor, ItineraryNode, ItineraryParallelTrack,
  ItineraryProfessionalSettings, ItineraryProfessionalSettingsData, ItinerarySegment,
  MajorCandidate, MovePointTarget, NodeDraft, ProfessionalAddContext, TimeSlot,
} from "../types";
import { defaultAdjacentTransport, hasExplicitProfessionalRoutes, rebuildAdjacentSegments, resolveDayEndpointNodes } from "./itineraryEditorService";

/** 专业按青绿、朱橙、宝蓝、莓紫、赭金区分；兼顾白底文字识别，颜色不进入保存契约。 */
export const PROFESSIONAL_COLORS = ["#00856a", "#c94b08", "#2563eb", "#b02d84", "#8a7400"] as const;
const slots: readonly TimeSlot[] = ["morning", "afternoon", "evening"];

/** 只为开放的考察分支补当天可确定的公共首尾，不改接已经明确的不同住宿或交通枢纽。 */
function shareOpenProfessionalEndpoints(day: ItineraryDay, paths: { nodeIds: string[] }[]): boolean {
  const ordered = [...day.nodes].sort((a, b) => slots.indexOf(a.timeSlot) - slots.indexOf(b.timeSlot) || a.sortOrder - b.sortOrder);
  const byId = new Map(ordered.map((node, index) => [node.id, { node, index }]));
  const isPublic = (id: string) => ["stay_area", "railway_station", "airport"].includes(byId.get(id)?.node.nodeType ?? "");
  let changed = false;
  for (const side of ["start", "end"] as const) {
    const endpoint = (path: { nodeIds: string[] }) => side === "start" ? path.nodeIds[0] : path.nodeIds.at(-1);
    const candidates = ordered.filter((node, index) => (
      isPublic(node.id) && paths.some((path) => !path.nodeIds.includes(node.id))
      && paths.every((path) => {
        if (path.nodeIds.includes(node.id)) return true;
        const id = endpoint(path);
        const boundary = id ? byId.get(id) : undefined;
        return boundary?.node.nodeType === "point"
          && (side === "start" ? index < boundary.index : index > boundary.index);
      })
    ));
    const candidate = side === "start" ? candidates.at(-1) : candidates[0];
    if (!candidate) continue;
    const prefixes = paths.filter((path) => path.nodeIds.includes(candidate.id)).map((path) => {
      const index = path.nodeIds.indexOf(candidate.id);
      return side === "start" ? path.nodeIds.slice(0, index + 1) : path.nodeIds.slice(index);
    });
    const shared = prefixes[0] ?? [candidate.id];
    // 只共享已知公共住宿/枢纽链，不能顺带把另一专业的考察点变成公共活动。
    if (!shared.every(isPublic) || prefixes.some((prefix) => JSON.stringify(prefix) !== JSON.stringify(shared))) continue;
    const missing = paths.filter((path) => !path.nodeIds.includes(candidate.id));
    if (missing.some((path) => shared.some((id) => path.nodeIds.includes(id)))) continue;
    for (const path of missing) {
      if (side === "start") path.nodeIds.unshift(...shared);
      else path.nodeIds.push(...shared);
      changed = true;
    }
  }
  return changed;
}

/** 从显式端点恢复专业路径；允许补确定的公共首尾，不猜测分支中间缺段。 */
export function normalizeExplicitProfessionalRoutes(editor: ItineraryEditor, idFactory: IdFactory): ItineraryEditor {
  const next = structuredClone(editor);
  for (const day of next.days) {
    if (day.parallelTracks?.tracks.length) {
      if (shareOpenProfessionalEndpoints(day, day.parallelTracks.tracks)) rebuildProfessionalDay(next, day.id, idFactory);
      continue;
    }
    if (!hasExplicitProfessionalRoutes(day, next.segments)) continue;
    const segments = next.segments.filter((segment) => segment.startDayId === day.id && segment.endDayId === day.id);
    const nodeIds = new Set(day.nodes.map((node) => node.id));
    if (segments.some((segment) => !nodeIds.has(segment.fromNodeId) || !nodeIds.has(segment.toNodeId))) continue;
    const majorIds = [...new Set(segments.flatMap((segment) => segment.majorId ? [segment.majorId] : []))];
    const paths = majorIds.map((majorId) => {
      const route = segments.filter((segment) => !segment.majorId || segment.majorId === majorId);
      const outgoing = new Map<string, ItinerarySegment>();
      const incoming = new Set<string>();
      for (const segment of route) {
        if (outgoing.has(segment.fromNodeId) || incoming.has(segment.toNodeId)) return null;
        outgoing.set(segment.fromNodeId, segment);
        incoming.add(segment.toNodeId);
      }
      const starts = [...outgoing.keys()].filter((id) => !incoming.has(id));
      if (starts.length !== 1) return null;
      const path = [starts[0]];
      const visited = new Set(path);
      while (outgoing.has(path.at(-1)!)) {
        const target = outgoing.get(path.at(-1)!)!.toNodeId;
        if (visited.has(target)) return null;
        path.push(target);
        visited.add(target);
      }
      return path.length === route.length + 1 ? { majorId, nodeIds: path } : null;
    });
    if (paths.some((path) => !path)) continue;
    const completePaths = paths.filter((path) => path !== null);
    shareOpenProfessionalEndpoints(day, completePaths);
    const first = completePaths[0];
    if (!first || completePaths.some((path) => path.nodeIds[0] !== first.nodeIds[0] || path.nodeIds.at(-1) !== first.nodeIds.at(-1))) continue;
    if (new Set(completePaths.flatMap((path) => path.nodeIds)).size !== nodeIds.size) continue;
    const professionalSettings = getItineraryProfessionalSettings(next);
    day.parallelTracks = {
      ...day.parallelTracks, version: 1,
      tracks: completePaths.map((path, index) => ({
        id: idFactory(), title: professionalSettings?.majors?.find((major) => major.id === path.majorId)?.name ?? `专业路线 ${index + 1}`,
        majorId: path.majorId, nodeIds: path.nodeIds, segmentIds: [],
      })),
    };
    rebuildProfessionalDay(next, day.id, idFactory);
  }
  return next;
}

/** 根据主专业优先的专业顺序返回固定色。 */
export function getProfessionalColor(majorId: string, majorIds: readonly string[]): string {
  return PROFESSIONAL_COLORS[Math.max(0, majorIds.indexOf(majorId)) % PROFESSIONAL_COLORS.length];
}

/** 创建专业设置，兼容旧调用方只传 ID 的形式。 */
export function createProfessionalSettings(
  majors: readonly (Pick<MajorCandidate, "id"> & Partial<Pick<MajorCandidate, "name">>)[],
  mainMajorId: string,
  previous?: ItineraryProfessionalSettings | null,
): ItineraryProfessionalSettings {
  const settings: ItineraryProfessionalSettings = { enabled: true, mainMajorId, majorIds: majors.map((major) => major.id) };
  const issues = validateProfessionalSettings(settings);
  if (issues.length) throw new Error(issues[0]);
  settings.majorIds = [mainMajorId, ...settings.majorIds.filter((id) => id !== mainMajorId)];
  if (majors.some((major) => major.name) || previous?.majors) settings.majors = settings.majorIds.map((id) => ({ id, name: majors.find((major) => major.id === id)?.name ?? previous?.majors?.find((major) => major.id === id)?.name ?? id }));
  return settings;
}

/** 校验专业数量及主专业归属。 */
export function validateProfessionalSettings(settings: ItineraryProfessionalSettings): string[] {
  if (!settings.enabled) return [];
  const ids = new Set(settings.majorIds.filter(Boolean));
  return [
    ...(ids.size < 1 ? ["至少选择一个专业"] : []),
    ...(ids.size > 5 ? ["最多选择五个专业"] : []),
    ...(!ids.has(settings.mainMajorId) ? ["主专业必须在已选择的专业中"] : []),
    ...(ids.size !== settings.majorIds.length ? ["专业不能重复"] : []),
  ];
}

/** 只读取行程主表专业设置；日级 tracks 仅表达实际路线分支。 */
export function getItineraryProfessionalSettings(editor: ItineraryEditor): (ItineraryProfessionalSettings & ItineraryProfessionalSettingsData) | null {
  const stored = editor.professionSettings;
  if (!stored?.majors?.length) return null;
  const ordered = [
    stored.majors.find((major) => major.id === stored.mainMajorId),
    ...stored.majors.filter((major) => major.id !== stored.mainMajorId),
  ].filter((major): major is { id: string; name: string } => Boolean(major));
  return { enabled: true, mainMajorId: stored.mainMajorId, majorIds: ordered.map((major) => major.id), majors: structuredClone(ordered) };
}

/** 保存行程主表专业设置；日级容器只保存实际 tracks，关闭时整行程仅保留主路径。 */
export function setItineraryProfessionalSettings(editor: ItineraryEditor, settings: ItineraryProfessionalSettingsData | ItineraryProfessionalSettings | null, idFactory: IdFactory): ItineraryEditor {
  const previous = getItineraryProfessionalSettings(editor);
  if (!settings || ("enabled" in settings && !settings.enabled)) return previous ? convertProfessionalToOrdinary(editor, "", previous.mainMajorId, idFactory) : editor;
  const majors = settings.majors ?? ("majorIds" in settings ? settings.majorIds.map((id) => ({ id, name: previous?.majors.find((major) => major.id === id)?.name ?? id })) : []);
  createProfessionalSettings(majors, settings.mainMajorId);
  const next = structuredClone(editor);
  if (!next.days.length) throw new Error("请先添加行程日");
  next.days.forEach((day) => {
    if (day.parallelTracks) day.parallelTracks = { version: 1, tracks: day.parallelTracks.tracks };
  });
  const retained = new Set(majors.map((major) => major.id));
  next.days.forEach((day) => {
    if (!day.parallelTracks) return;
    const original = day.parallelTracks.tracks;
    const remaining = original.filter((track) => retained.has(track.majorId));
    if (original.length && !remaining.length) {
      const main = original.find((track) => track.majorId === previous?.mainMajorId) ?? original[0];
      const byId = new Map(day.nodes.map((node) => [node.id, node]));
      const removed = new Set(day.nodes.filter((node) => !main.nodeIds.includes(node.id)).map((node) => node.id));
      const owned = new Set(original.flatMap((track) => track.segmentIds));
      const mainIds = new Set(main.segmentIds);
      day.nodes = main.nodeIds.map((id) => byId.get(id)!);
      for (const slot of slots) day.nodes.filter((node) => node.timeSlot === slot).forEach((node, index) => { node.sortOrder = index + 1; });
      next.segments = next.segments.filter((segment) => (!owned.has(segment.id) || mainIds.has(segment.id)) && !removed.has(segment.fromNodeId) && !removed.has(segment.toNodeId))
        .map((segment) => mainIds.has(segment.id) ? { ...segment, majorId: null } : segment);
      day.parallelTracks = null;
      return;
    }
    if (remaining.length !== original.length) {
      const kept = new Set(remaining.flatMap((track) => track.nodeIds));
      const removed = new Set(original.flatMap((track) => track.nodeIds).filter((id) => !kept.has(id)));
      day.nodes = day.nodes.filter((node) => !removed.has(node.id));
      next.segments = next.segments.filter((segment) => !removed.has(segment.fromNodeId) && !removed.has(segment.toNodeId) && (!segment.majorId || retained.has(segment.majorId)));
    }
    day.parallelTracks = remaining.length ? { version: 1, tracks: remaining } : null;
    if (remaining.length && !remaining.some((track) => track.majorId === settings.mainMajorId)) {
      remaining.push({ ...structuredClone(remaining[0]), id: idFactory(), majorId: settings.mainMajorId, title: majors.find((major) => major.id === settings.mainMajorId)!.name, segmentIds: [] });
    }
    remaining.sort((a, b) => Number(b.majorId === settings.mainMajorId) - Number(a.majorId === settings.mainMajorId));
    remaining.forEach((track) => { track.title = majors.find((major) => major.id === track.majorId)?.name ?? track.title; });
    if (remaining.length) rebuildProfessionalDay(next, day.id, idFactory);
  });
  next.professionSettings = {
    mainMajorId: settings.mainMajorId,
    majors: [majors.find((major) => major.id === settings.mainMajorId)!, ...majors.filter((major) => major.id !== settings.mainMajorId)].map(({ id, name }) => ({ id, name })),
  };
  next.segments = rebuildAdjacentSegments(next.days, next.segments, idFactory);
  return next;
}

/**
 * 调整行程专业顺序，只改行程级设置，不重建节点、路线段或交通事实。
 * 调用方传入数组的第一项作为主专业，其余专业按传入顺序保留。
 */
export function reorderItineraryProfessionals(editor: ItineraryEditor, orderedMajorIds: readonly string[]): ItineraryEditor {
  const settings = getItineraryProfessionalSettings(editor);
  if (!settings) throw new Error("请先设置行程专业");
  const currentIds = new Set(settings.majorIds);
  if (orderedMajorIds.length !== settings.majorIds.length || new Set(orderedMajorIds).size !== orderedMajorIds.length
    || orderedMajorIds.some((id) => !currentIds.has(id))) {
    throw new Error("专业顺序与当前行程设置不一致");
  }
  const mainMajorId = orderedMajorIds[0];
  const majorsById = new Map(settings.majors.map((major) => [major.id, major]));
  const orderedMajors = [
    mainMajorId,
    ...orderedMajorIds.filter((id) => id !== mainMajorId),
  ].map((id) => majorsById.get(id)!);
  const next = structuredClone(editor);
  const order = new Map(orderedMajors.map((major, index) => [major.id, index]));
  next.days.forEach((day) => {
    if (!day.parallelTracks) return;
    day.parallelTracks = {
      ...day.parallelTracks,
      tracks: day.parallelTracks.tracks
        .map((track, index) => ({ track, index }))
        .sort((left, right) => {
          const majorOrder = (order.get(left.track.majorId) ?? Number.MAX_SAFE_INTEGER)
            - (order.get(right.track.majorId) ?? Number.MAX_SAFE_INTEGER);
          return majorOrder || left.index - right.index;
        })
        .map(({ track }) => track),
    };
  });
  next.professionSettings = { mainMajorId, majors: orderedMajors.map(({ id, name }) => ({ id, name })) };
  return next;
}

/**
 * 替换一个行程专业的所有归属引用，保留点位、路线段身份和交通事实。
 * 目标专业已存在时拒绝合并，避免无确认地丢失专业分支。
 */
export function replaceItineraryProfessional(
  editor: ItineraryEditor,
  sourceMajorId: string,
  replacement: { id: string; name: string },
): ItineraryEditor {
  const settings = getItineraryProfessionalSettings(editor);
  if (!settings) throw new Error("请先设置行程专业");
  if (!settings.majorIds.includes(sourceMajorId)) throw new Error("找不到待替换专业");
  if (!replacement.id || settings.majorIds.includes(replacement.id)) throw new Error("替换目标专业已存在");
  const next = structuredClone(editor);
  next.days.forEach((day) => {
    if (!day.parallelTracks) return;
    day.parallelTracks = {
      ...day.parallelTracks,
      tracks: day.parallelTracks.tracks.map((track) => track.majorId === sourceMajorId
        ? { ...track, majorId: replacement.id, title: replacement.name }
        : track),
    };
  });
  next.segments = next.segments.map((segment) => segment.majorId === sourceMajorId
    ? { ...segment, majorId: replacement.id }
    : segment);
  const majors = settings.majors.map((major) => major.id === sourceMajorId ? replacement : major);
  const mainMajorId = settings.mainMajorId === sourceMajorId ? replacement.id : settings.mainMajorId;
  const majorsById = new Map(majors.map((major) => [major.id, major]));
  const orderedMajors = [
    mainMajorId,
    ...majors.map((major) => major.id).filter((id) => id !== mainMajorId),
  ].map((id) => majorsById.get(id)!);
  next.professionSettings = { mainMajorId, majors: orderedMajors.map(({ id, name }) => ({ id, name })) };
  return next;
}

/** 由实际轨道引用判断归属；开放首尾仍可属于单一专业。 */
export function getProfessionalNodeMajorId(editor: ItineraryEditor, dayId: string, nodeId: string): string | null {
  const day = editor.days.find((item) => item.id === dayId);
  const tracks = day?.parallelTracks?.tracks ?? [];
  const memberships = tracks.filter((track) => track.nodeIds.includes(nodeId));
  const node = day?.nodes.find((item) => item.id === nodeId);
  if (node && ["stay_area", "railway_station", "airport", "organization_site"].includes(node.nodeType)
    && memberships.some((track) => track.nodeIds[0] === nodeId || track.nodeIds.at(-1) === nodeId)) return null;
  return memberships.length === 1 ? memberships[0].majorId : null;
}

/** 专业归属与当前位置共同决定插入已有路径还是建立局部差异。 */
export function resolveProfessionalAddContext(editor: ItineraryEditor, dayId: string, sourceNodeId: string, selectedMajorId: string): ProfessionalAddContext {
  const day = editor.days.find((item) => item.id === dayId);
  if (!day?.nodes.some((node) => node.id === sourceNodeId)) throw new Error("找不到插入位置");
  const effective = getProfessionalNodeMajorId(editor, dayId, sourceNodeId) ?? selectedMajorId;
  const tracks = day.parallelTracks?.tracks ?? [];
  const track = tracks.find((item) => item.majorId === effective);
  const index = track?.nodeIds.indexOf(sourceNodeId) ?? -1;
  const hasExistingBranch = Boolean(track && index >= 0 && index < track.nodeIds.length - 1);
  return { sourceNodeId, selectedMajorId: effective, hasExistingBranch, action: hasExistingBranch ? "continue" : "create_parallel" };
}

/** 对单日专业路径重建相邻段，保留未变端点的身份和交通事实。 */
export function rebuildProfessionalDay(editor: ItineraryEditor, dayId: string, idFactory: IdFactory): ItineraryEditor {
  const day = requireProfessionalDay(editor, dayId);
  const tracks = day.parallelTracks?.tracks ?? [];
  const existing = editor.segments.filter((segment) => segment.startDayId === dayId && segment.endDayId === dayId);
  const nodes = new Map(day.nodes.map((node) => [node.id, node]));
  const used = new Set<string>();
  const rebuilt: ItinerarySegment[] = [];
  for (const [trackIndex, track] of tracks.entries()) {
    track.segmentIds = track.nodeIds.slice(0, -1).map((fromNodeId, index) => {
      const toNodeId = track.nodeIds[index + 1];
      const samePair = existing.filter((segment) => segment.fromNodeId === fromNodeId && segment.toNodeId === toNodeId);
      const preserved = samePair.find((segment) => segment.majorId === track.majorId && !used.has(segment.id))
        ?? (trackIndex === 0 ? samePair.find((segment) => !segment.majorId && !used.has(segment.id)) : undefined);
      const publicBoundary = [track.nodeIds.slice(0, index + 2), track.nodeIds.slice(index)].some((path) => (
        path.every((nodeId) => ["stay_area", "railway_station", "airport"].includes(nodes.get(nodeId)?.nodeType ?? ""))
      ));
      // 新共享的纯公共前后缀也可由次专业提供事实，存在多个候选时不猜选。
      const template = preserved ?? samePair.find((segment) => segment.majorId === tracks[0].majorId || !segment.majorId)
        ?? (publicBoundary && samePair.length === 1 ? samePair[0] : undefined);
      const id = preserved?.id ?? idFactory();
      used.add(id);
      rebuilt.push({ ...(template ? structuredClone(template) : defaultAdjacentTransport(nodes.get(fromNodeId), nodes.get(toNodeId), true)), id, majorId: track.majorId, startDayId: dayId, endDayId: dayId, routeSeq: index + 1, fromNodeId, toNodeId });
      return id;
    });
  }
  day.nodes.sort((a, b) => slots.indexOf(a.timeSlot) - slots.indexOf(b.timeSlot));
  for (const slot of slots) day.nodes.filter((node) => node.timeSlot === slot).forEach((node, index) => { node.sortOrder = index + 1; });
  editor.segments = [...editor.segments.filter((segment) => segment.startDayId !== dayId || segment.endDayId !== dayId), ...rebuilt];
  editor.segments = rebuildAdjacentSegments(editor.days, editor.segments, idFactory);
  return editor;
}

/** 查找目标日，避免失效 UI 上下文落入其他日期。 */
function requireProfessionalDay(editor: ItineraryEditor, dayId: string): ItineraryDay {
  const day = editor.days.find((item) => item.id === dayId);
  if (!day) throw new Error("找不到行程日");
  return day;
}

/** 首次专业差异出现时才把普通路径投影为主专业轨道。 */
function ensureMainTrack(editor: ItineraryEditor, dayId: string, idFactory: IdFactory): ItineraryParallelTrack {
  const day = requireProfessionalDay(editor, dayId);
  const settings = getItineraryProfessionalSettings(editor);
  if (!settings) throw new Error("请先设置行程专业");
  const found = day.parallelTracks?.tracks.find((track) => track.majorId === settings.mainMajorId);
  if (found) return found;
  const path = resolveDayEndpointNodes(day).map((node) => node.id);
  if (!path.length) throw new Error("专业分流需要先添加考察点");
  const main = { id: idFactory(), title: settings.majors.find((major) => major.id === settings.mainMajorId)!.name, majorId: settings.mainMajorId, nodeIds: path, segmentIds: [] };
  day.parallelTracks = { ...day.parallelTracks, version: 1, tracks: [main, ...(day.parallelTracks?.tracks ?? [])] };
  return main;
}

/** 按目标专业插入节点，新专业只替换相邻一处并共享其余路径。 */
export function insertProfessionalNode(editor: ItineraryEditor, dayId: string, anchorId: string, placement: "before" | "after", draft: NodeDraft, timeSlot: TimeSlot, idFactory: IdFactory, majorId?: string | null): ItineraryEditor {
  const next = structuredClone(editor);
  const settings = getItineraryProfessionalSettings(next);
  if (!settings) throw new Error("请先设置行程专业");
  const day = requireProfessionalDay(next, dayId);
  const locked = getProfessionalNodeMajorId(next, dayId, anchorId);
  const mainPath = resolveDayEndpointNodes(day);
  const mainAnchorIndex = mainPath.findIndex((item) => item.id === anchorId);
  const isOpenPointIndex = (index: number) => mainPath[index]?.nodeType === "point"
    && (index === 0 || index === mainPath.length - 1)
    && (mainPath.length === 1 || ["stay_area", "railway_station", "airport"].includes(mainPath[index === 0 ? mainPath.length - 1 : 0].nodeType));
  const newBoundaryMajor = (draft.nodeType ?? "point") === "point" && majorId
    && !day.parallelTracks?.tracks.some((item) => item.majorId === majorId)
    && mainPath.some((_, index) => isOpenPointIndex(index)
      && (index === mainAnchorIndex || index === mainAnchorIndex + (placement === "after" ? 1 : -1)));
  const selected = newBoundaryMajor ? majorId : locked ?? majorId ?? settings.mainMajorId;
  if (!settings.majorIds.includes(selected)) throw new Error("所选专业不在行程设置中");
  const main = ensureMainTrack(next, dayId, idFactory);
  let track = day.parallelTracks!.tracks.find((item) => item.majorId === selected);
  const creatingTrack = !track;
  if (!track) {
    track = { id: idFactory(), majorId: selected, title: settings.majors.find((major) => major.id === selected)!.name, nodeIds: [...main.nodeIds], segmentIds: [] };
    day.parallelTracks!.tracks.push(track);
  }
  const anchorIndex = track.nodeIds.indexOf(anchorId);
  if (anchorIndex < 0) throw new Error("当前点位不属于所选专业线路");
  const adjacentIndex = anchorIndex + (placement === "after" ? 1 : -1);
  const adjacent = track.nodeIds[adjacentIndex];
  const openPointIndex = creatingTrack && (draft.nodeType ?? "point") === "point"
    ? [anchorIndex, adjacentIndex].find(isOpenPointIndex)
    : undefined;
  const commonEndpoint = !adjacent && ["stay_area", "railway_station", "airport"].includes(draft.nodeType ?? "point");
  if (!adjacent && !commonEndpoint && openPointIndex === undefined) throw new Error("请在公共起终点之间插入点位");
  if (commonEndpoint) {
    const tracks = day.parallelTracks!.tracks;
    const boundary = (item: ItineraryParallelTrack) => placement === "before" ? item.nodeIds[0] : item.nodeIds.at(-1);
    const opposite = (item: ItineraryParallelTrack) => placement === "before" ? item.nodeIds.at(-1) : item.nodeIds[0];
    const unsafe = tracks.some((item) => {
      const endpointId = boundary(item);
      const endpoint = day.nodes.find((candidate) => candidate.id === endpointId);
      const differentPublicEndpoint = endpointId !== anchorId && (
        (endpoint && ["stay_area", "railway_station", "airport", "organization_site"].includes(endpoint.nodeType))
        || tracks.filter((candidate) => boundary(candidate) === endpointId).length > 1
      );
      const related = endpointId === anchorId || opposite(item) === opposite(track)
        || (item.nodeIds.length === 1 && track.nodeIds.length === 1);
      return !related || differentPublicEndpoint;
    });
    if (unsafe) throw new Error("并行线路的公共入口或出口不同，无法确认同组路径，请分别调整已有端点。");
  }
  const id = idFactory();
  const node: ItineraryNode = { ...draft, id, title: draft.title, nodeType: draft.nodeType ?? "point", timeSlot, sortOrder: 1 };
  const from = placement === "after" ? anchorId : adjacent;
  const to = placement === "after" ? adjacent : anchorId;
  const mainIndex = main.nodeIds.indexOf(anchorId);
  const replace = selected !== settings.mainMajorId && !locked
    && adjacent === main.nodeIds[mainIndex + (placement === "after" ? 1 : -1)]
    && adjacentIndex > 0 && adjacentIndex < track.nodeIds.length - 1;
  if (replace) track.nodeIds.splice(adjacentIndex, 1, id);
  else if (openPointIndex !== undefined) track.nodeIds.splice(openPointIndex, 1, id);
  else if (commonEndpoint) {
    day.parallelTracks!.tracks.forEach((item) => {
      if (placement === "before") item.nodeIds.unshift(id);
      else item.nodeIds.push(id);
    });
  } else if (selected === settings.mainMajorId) {
    day.parallelTracks!.tracks.forEach((item) => {
      const index = item.nodeIds.indexOf(from);
      if (index >= 0 && item.nodeIds[index + 1] === to) item.nodeIds.splice(index + 1, 0, id);
    });
  } else track.nodeIds.splice(anchorIndex + (placement === "after" ? 1 : 0), 0, id);
  const anchorGlobal = day.nodes.findIndex((item) => item.id === anchorId);
  day.nodes.splice(anchorGlobal + (placement === "after" ? 1 : 0), 0, node);
  return rebuildProfessionalDay(next, dayId, idFactory);
}

/** 删除专业点位并同步所有引用路径，保留公共首尾。 */
export function deleteProfessionalNode(editor: ItineraryEditor, dayId: string, nodeId: string, idFactory: IdFactory): ItineraryEditor {
  const next = structuredClone(editor);
  const day = requireProfessionalDay(next, dayId);
  const tracks = day.parallelTracks!.tracks;
  if (tracks.some((track) => track.nodeIds[0] === nodeId || track.nodeIds.at(-1) === nodeId)) throw new Error("公共首尾点位请通过编辑替换");
  const affected = tracks.filter((track) => track.nodeIds.includes(nodeId));
  const originalPath = affected[0]?.nodeIds ?? [];
  const position = originalPath.indexOf(nodeId);
  const shared = (id: string) => tracks.every((track) => track.nodeIds.includes(id));
  const from = originalPath.slice(0, position).reverse().find(shared);
  const to = originalPath.slice(position + 1).find(shared);
  tracks.forEach((track) => { track.nodeIds = track.nodeIds.filter((id) => id !== nodeId); });
  day.nodes = day.nodes.filter((node) => node.id !== nodeId);
  if (from && to && affected.length < tracks.length) {
    const sections = tracks.map((track) => ({
      track,
      nodes: track.nodeIds.slice(track.nodeIds.indexOf(from) + 1, track.nodeIds.indexOf(to)),
    }));
    const remaining = sections.filter((section) => section.nodes.length);
    const survivor = remaining[0];
    // 只恢复本次删除清空的局部分支；多个不同有效路径并存时不能擅自替用户选线。
    if (survivor && remaining.every((section) => JSON.stringify(section.nodes) === JSON.stringify(survivor.nodes))) {
      for (const { track, nodes: section } of sections) {
        if (section.length || !affected.includes(track)) continue;
        track.nodeIds.splice(track.nodeIds.indexOf(from) + 1, 0, ...survivor.nodes);
        const path = [from, ...survivor.nodes, to];
        for (let index = 0; index < path.length - 1; index += 1) {
          const template = next.segments.find((segment) => survivor.track.segmentIds.includes(segment.id)
            && segment.fromNodeId === path[index] && segment.toNodeId === path[index + 1]);
          if (template) next.segments.push({ ...structuredClone(template), id: idFactory(), majorId: track.majorId });
        }
      }
    }
  }
  return rebuildProfessionalDay(next, dayId, idFactory);
}

/** 专业移动只调整该点所在路径，目标索引采用当前日可见节点索引。 */
export function moveProfessionalNode(editor: ItineraryEditor, dayId: string, nodeId: string, target: MovePointTarget, idFactory: IdFactory, majorId?: string | null): ItineraryEditor {
  const next = structuredClone(editor);
  const day = requireProfessionalDay(next, dayId);
  const node = day.nodes.find((item) => item.id === nodeId)!;
  const paths = day.parallelTracks?.tracks.filter((track) => track.nodeIds.includes(nodeId)) ?? [];
  const originalTrackIndexes = new Map(paths.map((track) => [track.id, track.nodeIds.indexOf(nodeId)]));
  if (dayId !== target.dayId) {
    if (paths.some((track) => track.nodeIds[0] === nodeId || track.nodeIds.at(-1) === nodeId)) throw new Error("公共首尾点位请通过编辑替换");
    const settings = getItineraryProfessionalSettings(next)!;
    const selected = getProfessionalNodeMajorId(next, dayId, nodeId) ?? majorId ?? settings.mainMajorId;
    const targetDay = requireProfessionalDay(next, target.dayId);
    let targetTrack: ItineraryParallelTrack | undefined;
    if (targetDay.parallelTracks?.tracks.length || selected !== settings.mainMajorId) {
      const main = ensureMainTrack(next, target.dayId, idFactory);
      targetTrack = targetDay.parallelTracks!.tracks.find((track) => track.majorId === selected);
      if (!targetTrack) {
        targetTrack = { ...structuredClone(main), id: idFactory(), majorId: selected, title: settings.majors.find((major) => major.id === selected)?.name ?? selected, segmentIds: [] };
        targetDay.parallelTracks!.tracks.push(targetTrack);
      }
    }
    const candidateIds = targetDay.nodes.filter((item) => item.timeSlot === target.timeSlot).slice(Math.max(0, target.index)).map((item) => item.id);
    if (targetTrack) {
      const beforeId = candidateIds.find((id) => targetTrack!.nodeIds.includes(id));
      const index = Math.max(1, beforeId ? targetTrack.nodeIds.indexOf(beforeId) : targetTrack.nodeIds.length - 1);
      const from = targetTrack.nodeIds[index - 1];
      const to = targetTrack.nodeIds[index];
      const affected = selected === settings.mainMajorId ? targetDay.parallelTracks!.tracks : [targetTrack];
      affected.forEach((track) => {
        const fromIndex = track.nodeIds.indexOf(from);
        if (fromIndex >= 0 && track.nodeIds[fromIndex + 1] === to) track.nodeIds.splice(fromIndex + 1, 0, nodeId);
      });
    }
    paths.forEach((track) => { track.nodeIds = track.nodeIds.filter((id) => id !== nodeId); });
    day.nodes = day.nodes.filter((item) => item.id !== nodeId);
    node.timeSlot = target.timeSlot;
    const globalIndex = candidateIds[0] ? targetDay.nodes.findIndex((item) => item.id === candidateIds[0]) : targetDay.nodes.length;
    targetDay.nodes.splice(globalIndex, 0, node);
    if (paths.length) rebuildProfessionalDay(next, dayId, idFactory);
    if (targetTrack) rebuildProfessionalDay(next, target.dayId, idFactory);
    for (const changedDay of [day, targetDay]) for (const slot of slots) changedDay.nodes.filter((item) => item.timeSlot === slot).forEach((item, index) => { item.sortOrder = index + 1; });
    next.segments = rebuildAdjacentSegments(next.days, next.segments, idFactory);
    return next;
  }
  const sameSlotPairs = node.timeSlot === target.timeSlot ? next.segments.filter((segment) => (
    (segment.transportMode === "rail" || segment.transportMode === "air")
    && segment.startDayId === dayId && segment.endDayId === dayId
    && day.nodes.find((item) => item.id === segment.fromNodeId)?.timeSlot === target.timeSlot
    && day.nodes.find((item) => item.id === segment.toNodeId)?.timeSlot === target.timeSlot
    && paths.some((track) => track.segmentIds.includes(segment.id))
  )) : [];
  if (sameSlotPairs.length) {
    const beforeIds = day.nodes.filter((item) => item.id !== nodeId && item.timeSlot === target.timeSlot)
      .slice(Math.max(0, target.index)).map((item) => item.id);
    const movingIds = new Set([nodeId]);
    let changed = false;
    for (const track of paths) {
      const original = track.nodeIds;
      const beforeId = beforeIds.find((id) => original.includes(id));
      const originalIndex = original.indexOf(nodeId);
      if (original[originalIndex + 1] === beforeId) continue;
      // 只检查节点实际所属轨道；别的专业的站对不能改变本次落点是否合法。
      const pairs = sameSlotPairs.filter((segment) => track.segmentIds.includes(segment.id));
      const group = new Set([nodeId]);
      for (let count = 0; count < pairs.length; count += 1) {
        for (const pair of pairs) if (group.has(pair.fromNodeId)) group.add(pair.toNodeId);
      }
      if (pairs.some((pair) => pair.toNodeId === nodeId)
        || (beforeId && (group.has(beforeId)
          || pairs.some((pair) => pair.toNodeId === beforeId && !group.has(pair.fromNodeId))))) {
        throw new Error("铁路或航班站对必须保持连续，请选择整段交通之外的位置。");
      }
      const remaining = original.filter((id) => !group.has(id));
      const targetSlotIndex = slots.indexOf(target.timeSlot);
      const nextSlotIndex = remaining.findIndex((id) => {
        const remainingNode = day.nodes.find((item) => item.id === id);
        return remainingNode && slots.indexOf(remainingNode.timeSlot) > targetSlotIndex;
      });
      const terminalIndex = remaining.indexOf(original.at(-1)!);
      const requestedIndex = beforeId
        ? remaining.indexOf(beforeId)
        : nextSlotIndex >= 0 ? nextSlotIndex : remaining.length;
      const insertAt = Math.max(
        1,
        Math.min(requestedIndex, terminalIndex >= 0 ? terminalIndex : remaining.length),
      );
      const reordered = [...remaining];
      reordered.splice(insertAt, 0, ...original.filter((id) => group.has(id)));
      if (reordered[0] !== original[0] || reordered.at(-1) !== original.at(-1)) {
        throw new Error("公共首尾点位请通过编辑替换，铁路或航班站对必须保持连续。");
      }
      track.nodeIds = reordered;
      group.forEach((id) => movingIds.add(id));
      changed ||= reordered.some((id, index) => id !== original[index]);
    }
    if (!changed) return next;
    const movingNodes = day.nodes.filter((item) => movingIds.has(item.id));
    day.nodes = day.nodes.filter((item) => !movingIds.has(item.id));
    const beforeId = beforeIds.find((id) => !movingIds.has(id));
    const targetSlotIndex = slots.indexOf(target.timeSlot);
    const nextSlotIndex = day.nodes.findIndex((item) => slots.indexOf(item.timeSlot) > targetSlotIndex);
    const index = beforeId
      ? day.nodes.findIndex((item) => item.id === beforeId)
      : nextSlotIndex >= 0 ? nextSlotIndex : day.nodes.length;
    day.nodes.splice(index, 0, ...movingNodes);
    return rebuildProfessionalDay(next, dayId, idFactory);
  }
  if (paths.some((track) => track.nodeIds[0] === nodeId || track.nodeIds.at(-1) === nodeId)) {
    node.timeSlot = target.timeSlot;
    return rebuildProfessionalDay(next, dayId, idFactory);
  }
  const candidates = day.nodes.filter((item) => item.id !== nodeId && item.timeSlot === target.timeSlot);
  const beforeIds = candidates.slice(Math.max(0, target.index)).map((item) => item.id);
  paths.forEach((track) => {
    track.nodeIds = track.nodeIds.filter((id) => id !== nodeId);
    const beforeId = beforeIds.find((id) => track.nodeIds.includes(id));
    const originalIndex = originalTrackIndexes.get(track.id) ?? track.nodeIds.length - 1;
    // 跨时段没有同专业前置点时只改变时段，保留原阶段，避免点位落到汇合点之后导致整条专业线倒序。
    const insertionIndex = beforeId ? track.nodeIds.indexOf(beforeId) : Math.min(originalIndex, track.nodeIds.length - 1);
    track.nodeIds.splice(Math.max(1, insertionIndex), 0, nodeId);
  });
  node.timeSlot = target.timeSlot;
  day.nodes = day.nodes.filter((item) => item.id !== nodeId);
  const beforeIndex = beforeIds[0] ? day.nodes.findIndex((item) => item.id === beforeIds[0]) : day.nodes.length;
  day.nodes.splice(beforeIndex, 0, node);
  return rebuildProfessionalDay(next, dayId, idFactory);
}

/** 整行程回退清除专业配置，保留主路径及其他日期和跨日交通。 */
export function convertProfessionalToOrdinary(editor: ItineraryEditor, _dayId: string, mainMajorId: string, idFactory: IdFactory): ItineraryEditor {
  const next = structuredClone(editor);
  const removedNodes = new Set<string>();
  const removedSegments = new Set<string>();
  const retainedSegments = new Set<string>();
  for (const day of next.days) {
    const tracks = day.parallelTracks?.tracks ?? [];
    if (tracks.length) {
      const main = tracks.find((track) => track.majorId === mainMajorId);
      if (!main) throw new Error("找不到主专业轨道");
      const byId = new Map(day.nodes.map((node) => [node.id, node]));
      day.nodes.filter((node) => !main.nodeIds.includes(node.id)).forEach((node) => removedNodes.add(node.id));
      day.nodes = main.nodeIds.map((id) => byId.get(id)!);
      for (const slot of slots) day.nodes.filter((node) => node.timeSlot === slot).forEach((node, index) => { node.sortOrder = index + 1; });
      tracks.flatMap((track) => track.segmentIds).forEach((id) => removedSegments.add(id));
      main.segmentIds.forEach((id) => retainedSegments.add(id));
    }
    day.parallelTracks = null;
  }
  next.segments = next.segments.filter((segment) => (!removedSegments.has(segment.id) || retainedSegments.has(segment.id)) && !removedNodes.has(segment.fromNodeId) && !removedNodes.has(segment.toNodeId))
    .map((segment) => segment.majorId ? { ...segment, majorId: null } : segment);
  next.professionSettings = null;
  next.segments = rebuildAdjacentSegments(next.days, next.segments, idFactory);
  return next;
}

/** 返回交通段当前归属。 */
export function getSegmentProfessionalId(editor: ItineraryEditor, segmentId: string): string | null {
  return editor.segments.find((segment) => segment.id === segmentId)?.majorId ?? null;
}

/** 固定专业上下文供现有交通草稿复用。 */
export function withProfessionalSegmentContext(segment: ItinerarySegment, majorId: string | null): ItinerarySegment {
  return { ...structuredClone(segment), majorId };
}
